"use strict";

// Return the ID of the straight product
function getStraightProductId(productConfig, productId) {
  /**
   * Returns the id of the straight product that corresponds with productId
   * @param {Array} productConfig - array of all products available in this platform
   * @param {string} productId - Id reference of the target product
   */
  const { GasoilId, DieselId, BlendedGasoilId, BlendedDieselBioId } = productConfig;
  switch (productId) {
    case BlendedDieselBioId:
      return DieselId;
    case BlendedGasoilId:
      return GasoilId;
    default:
      return productId;
  }
}

function getBaseProductId(productConfig, productId) {
  /**
   * The function either:
   * Returns the straight product ID that corresponds with the productId of a blended product
   * Returns the base settlement ID that corresponds with the productId of a straight product
   * @param {Array} productConfig - array of all products available in this platform
   * @param {string} productId - Id reference of the target product
   *
   * Note that this function is now @deprecated
   * To get the straight product of a blended product, use getStraightProductId
   * To get the base settlement product of a straight product, use getBaseSettlementId
   */
  const { GasoilId, DieselId, BioDieselId, BlendedGasoilId, BlendedDieselBioId, BioDieselForGasoilId } = productConfig;
  switch (productId) {
    case GasoilId:
    case BlendedDieselBioId:
      return DieselId;
    case BlendedGasoilId:
      return GasoilId;
    case BioDieselForGasoilId:
      return BioDieselId;
    default:
      return productId;
  }
}

// Return the ID of the bio product for a given product
function getBioProductId(productConfig, productId) {
  const { BlendedDieselBioId, BioDieselId, BlendedGasoilId, BioDieselForGasoilId } = productConfig;
  switch (productId) {
    case BlendedDieselBioId:
      return BioDieselId;
    case BlendedGasoilId:
      return BioDieselForGasoilId;
  }
}

// Return the product ID used for settlement reference
function getBaseSettlementId(productConfig, productId) {
  /**
   * Returns the id of the base settlement product that corresponds with productId
   * @param {Array} productConfig - array of all products available in this platform
   * @param {string} productId - Id reference of the target product
   */
  const {
    GasoilId,
    DieselId,
    BlendedDieselBioId,
    BlendedGasoilId,
    BioDieselId,
    BioDieselForGasoilId,
    PetrolId,
    PetrolWithAdditiveId,
    GasolineId,
    SU97Id,
    SU99Id,
    DieselWithAdditiveId,
    GasoilFame0Id,
    HVOId,
    MarineGasoilId,
    HighBioMarineDieselId,
    PremiumDieselId,
  } = productConfig;
  switch (productId) {
    case GasoilId:
    case BlendedGasoilId:
    case BlendedDieselBioId:
    case DieselWithAdditiveId:
    case GasoilFame0Id:
    case HVOId:
    case MarineGasoilId:
    case HighBioMarineDieselId:
    case PremiumDieselId:
      return DieselId;
    case BioDieselForGasoilId:
      return BioDieselId;
    case PetrolId:
    case PetrolWithAdditiveId:
    case SU97Id:
    case SU99Id:
      return GasolineId;
    default:
      return productId;
  }
}

// Return the ID of the bio product used for settlement reference
function getBioSettlementId(productConfig, productId) {
  const { BlendedDieselBioId, BlendedGasoilId, BioDieselId } = productConfig;
  switch (productId) {
    case BlendedGasoilId:
    case BlendedDieselBioId:
      return BioDieselId;
    default:
      return productId;
  }
}

// Return the ID of a product whose density is used as a reference by a given product
function getProductDensityReferenceId(productConfig, productId) {
  const { BioDieselForGasoilId, BioDieselId } = productConfig;
  switch (productId) {
    case BioDieselForGasoilId:
      return BioDieselId;
    default:
      return productId;
  }
}

// Return the ID of a product whose duty is used as a reference by a given product
function getProductDutyReferenceId(productConfig, productId) {
  const { BioDieselId, DieselId, BioDieselForGasoilId, GasoilId } = productConfig;
  switch (productId) {
    case BioDieselId:
      return DieselId;
    case BioDieselForGasoilId:
      return GasoilId;
    default:
      return productId;
  }
}

// Return the product IDs relevant to a given product (e.g. base and bio element IDs for a blended product)
function getProductRangeIds(productConfig, productId) {
  const blendedProductIds = [productConfig.BlendedDieselBioId, productConfig.BlendedGasoilId];
  const isBlendedProduct = blendedProductIds.includes(productId);
  let productRangeIds = [productId]; // Create a range of product IDs to match during filtering

  // If this is blended product, let's add the straight product and bio product to the product range
  if (isBlendedProduct) {
    const straightProductId = getStraightProductId(productConfig, productId);
    productRangeIds.push(straightProductId);
    const bioProductId = getBioSettlementId(productConfig, productId);
    productRangeIds.push(bioProductId);
  }

  return productRangeIds;
}

// Return the straight and blended product IDs for a given product
function getStraightAndBlendedProductIds(productConfig, productId) {
  const dieselBasedProducts = [productConfig.DieselId, productConfig.BlendedDieselBioId];
  const gasoilBasedProducts = [productConfig.GasoilId, productConfig.BlendedGasoilId];

  if (dieselBasedProducts.includes(productId)) {
    return dieselBasedProducts;
  }
  if (gasoilBasedProducts.includes(productId)) {
    return gasoilBasedProducts;
  }
  return [productId];
}

function isBlendedProduct(productConfig, productId) {
  return [productConfig.BlendedDieselBioId, productConfig.BlendedGasoilId].includes(productId);
}

function filterSettlementProducts(settlementProducts, availableProducts, productConfig) {
  const { GasolineId } = productConfig;
  const containsGasolineSettlement = availableProducts
    .map((product) => getBaseSettlementId(productConfig, product._id))
    .includes(GasolineId);
  return settlementProducts.filter(
    ({ _id }) => _id !== GasolineId || (_id === GasolineId && containsGasolineSettlement)
  );
}

export {
  getStraightProductId,
  getBaseProductId,
  getBioProductId,
  getBaseSettlementId,
  getBioSettlementId,
  getProductDensityReferenceId,
  getProductDutyReferenceId,
  getProductRangeIds,
  getStraightAndBlendedProductIds,
  filterSettlementProducts,
  isBlendedProduct,
};
